import {
  Component,
  Input,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Inject,
} from '@angular/core';
import { CourseTrack, Lesson } from '../shared/models';
import { AcademyNavigationService } from '../services/academy-navigation.service';
import { AcademyCycleSignUpService } from '../services/academy-cycle-sign-up.service';
import {
  isPartOfLearningTrack,
  learningTrackHeaderTitle,
  learningTrackCoursesCompleted,
  learningTrackCoursesTotal,
} from '../shared/course-track.utils';

/**
 * Course page container component for Academy course tracks.
 * Manages lesson display, navigation, and progress tracking.
 */
@Component({
  selector: 'ut-course-page',
  templateUrl: './course-page.component.html',
  styleUrls: ['./course-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CoursePageComponent implements OnInit {
  @Input() courseTrack!: CourseTrack;
  @Input() user: any;
  @Input() useRedesignCatalog = false;

  currentLesson: Lesson | null = null;
  previousLesson: Lesson | null = null;
  nextLesson: Lesson | null = null;
  showSignUpConfirmation = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private academyNavigationService: AcademyNavigationService,
    private cycleSignUpService: AcademyCycleSignUpService,
    @Inject('CourseTrackService') private CourseTrackService: any,
    @Inject('CourseService') private CourseService: any,
    @Inject('$state') private $state: any,
    @Inject('$rootScope') private $rootScope: any,
  ) {}

  get isPartOfLearningTrack(): boolean {
    return isPartOfLearningTrack(this.courseTrack, this.useRedesignCatalog);
  }

  get isLockedByOrder(): boolean {
    return !!this.courseTrack?.user_is_locked_by_learning_track_order;
  }

  get headerTitle(): string {
    return learningTrackHeaderTitle(this.courseTrack, this.useRedesignCatalog);
  }

  get trackCoursesCompleted(): number {
    return learningTrackCoursesCompleted(this.courseTrack);
  }

  get trackCoursesTotal(): number {
    return learningTrackCoursesTotal(this.courseTrack);
  }

  ngOnInit(): void {
    if (this.courseTrack && !this.isLockedByOrder) {
      this.initializeLesson();
    }
  }

  private initializeLesson(): void {
    // Use the URL slug to navigate between lessons.
    // Fall back to first unviewed for initial course entry.
    const courseSlug = this.$state.params?.lessonId || this.$state.params?.courseId;
    const lessonFromUrl = courseSlug
      ? this.courseTrack.courses?.find((lesson) => lesson.slug === courseSlug) || null
      : null;
    const firstUnviewed = this.courseTrack.courses?.find(
      (lesson) => !lesson.viewed_by_current_user,
    ) || null;
    this.currentLesson = lessonFromUrl || firstUnviewed
      || this.courseTrack?.current_course || this.courseTrack.courses?.[0] || null;

    if (this.currentLesson) {
      this.updateNavigationState();
      this.trackLessonView(this.currentLesson.id);

      // URL must always include lessonId on the new route schema.
      // When arriving without a lessonId (e.g. initial "Start Course" click or
      // old-URL redirect), update the URL to include the resolved lesson slug.
      if (!courseSlug && this.$state.current.name === 'learning-track-course') {
        this.$state.go(
          'learning-track-course',
          {
            learningTrackId: this.$state.params.learningTrackId,
            courseTrackId: this.$state.params.courseTrackId,
            lessonId: this.currentLesson.slug,
          },
          { notify: false, location: 'replace' },
        );
      }
    }
  }

  private updateNavigationState(): void {
    if (!this.courseTrack?.courses || !this.currentLesson) {
      this.previousLesson = null;
      this.nextLesson = null;
      return;
    }

    const currentIndex = this.courseTrack.courses.findIndex(
      (lesson) => lesson.id === this.currentLesson!.id,
    );

    if (currentIndex === -1) {
      this.previousLesson = null;
      this.nextLesson = null;
      return;
    }

    this.previousLesson = this.courseTrack.courses[currentIndex - 1] || null;
    this.nextLesson = this.courseTrack.courses[currentIndex + 1] || null;
  }

  private async trackLessonView(lessonId: number): Promise<void> {
    if (!this.CourseService || !lessonId) return;

    this.markLessonAsViewed(lessonId);
    this.cdr.markForCheck();
    try {
      await this.CourseService.addPageView(lessonId);
      await this.checkAndUpdateProgress();
    } catch (err) {
      console.error('Failed to record page view for lesson', lessonId, err);
    } finally {
      this.cdr.markForCheck();
    }
  }

  private markLessonAsViewed(lessonId: number): void {
    if (!this.courseTrack?.courses) return;

    const lessonIndex = this.courseTrack.courses.findIndex(l => l.id === lessonId);
    if (lessonIndex === -1 || this.courseTrack.courses[lessonIndex].viewed_by_current_user) return;

    const updatedCourses = [...this.courseTrack.courses];
    updatedCourses[lessonIndex] = { ...updatedCourses[lessonIndex], viewed_by_current_user: true };
    this.courseTrack = { ...this.courseTrack, courses: updatedCourses };
  }

  private async checkAndUpdateProgress(): Promise<void> {
    if (!this.courseTrack?.courses) return;

    const allCoursesViewed = this.courseTrack.courses.every(
      (lesson) => lesson.viewed_by_current_user,
    );

    if (allCoursesViewed && !this.courseTrack.user_has_viewed_all_courses) {
      const res = await this.CourseTrackService.updateCourseTrackProgress(this.courseTrack.id, {
        viewed_all_courses: true,
      });
      this.courseTrack = {
        ...this.courseTrack,
        user_has_viewed_all_courses: res.data.user_has_viewed_all_courses,
        user_can_take_quiz: res.data.user_can_take_quiz,
        user_can_apply_to_test_cycle: res.data.user_can_apply_to_test_cycle,
        user_has_completed_course_track: res.data.user_has_completed_course_track,
      };
      this.cdr.markForCheck();
    }
  }

  private scrollToTop(): void {
    const mainContentContainer = document.getElementById('main-content-container');
    if (mainContentContainer) {
      mainContentContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  onLessonSelect(lesson: Lesson): void {
    if (lesson.id === this.currentLesson?.id) return;

    this.scrollToTop();

    this.currentLesson = lesson;
    this.updateNavigationState();
    this.trackLessonView(lesson.id);
    this.cdr.markForCheck();

    // notify:false skips the meta module's transition hook; set document.title immediately
    // and $rootScope.meta.title so the next AngularJS digest doesn't revert it.
    const lessonTitle = lesson.title || '';
    const title = lessonTitle
      ? `uTest Academy Course - ${this.courseTrack.name}: ${lessonTitle}`
      : `uTest Academy Course - ${this.courseTrack.name}`;
    this.$rootScope.meta.title = title;
    document.title = title;

    // Update URL without page reload.
    // Pass preloadedCourseTrack so the resolver skips the API fetch and
    // preserves our optimistic viewed-state updates (viewed_by_current_user).
    const stateName = this.isPartOfLearningTrack ? 'learning-track-course' : 'track-course';
    const params = this.isPartOfLearningTrack
      ? {
        learningTrackId: this.courseTrack.learning_track_id,
        courseTrackId: this.courseTrack.id,
        lessonId: lesson.slug,
        preloadedCourseTrack: this.courseTrack,
      }
      : { trackId: this.courseTrack.id, courseId: lesson.slug };
    this.$state.go(stateName, params, { notify: false });
  }

  onPreviousLesson(): void {
    if (this.previousLesson) {
      this.onLessonSelect(this.previousLesson);
    }
  }

  onContinueLesson(): void {
    if (this.nextLesson) {
      this.onLessonSelect(this.nextLesson);
    }
  }

  onBackToTracks(): void {
    this.academyNavigationService.goToAcademyHome();
  }

  get completedLessonsCount(): number {
    if (!this.courseTrack?.courses) return 0;
    return this.courseTrack.courses.filter(
      (lesson) => lesson.viewed_by_current_user,
    ).length;
  }

  get totalLessonsCount(): number {
    return this.courseTrack?.courses?.length || 0;
  }

  get hasPreviousLesson(): boolean {
    return !!this.previousLesson;
  }

  get hasNextLesson(): boolean {
    return !!this.nextLesson;
  }

  get isFirstLesson(): boolean {
    return !this.previousLesson;
  }

  get isLastLesson(): boolean {
    return !this.nextLesson;
  }

  get hasQuiz(): boolean {
    return !!this.courseTrack?.has_quiz;
  }

  get isQuizAvailable(): boolean {
    return (
      this.hasQuiz
      && !!this.courseTrack?.user_has_viewed_all_courses
      && !this.courseTrack?.user_has_passed_quiz
    );
  }

  get isQuizLocked(): boolean {
    return this.hasQuiz && !this.courseTrack?.user_has_viewed_all_courses;
  }

  onNavigateToQuiz(): void {
    if (!this.isQuizAvailable) {
      return;
    }
    if (this.courseTrack?.id) {
      this.academyNavigationService.goToQuiz(this.courseTrack);
    }
  }

  async onCycleSignUp(): Promise<void> {
    const result = await this.cycleSignUpService.signUp(this.courseTrack, this.user, this.cdr);
    if (result) {
      this.courseTrack = result.courseTrack;
      this.showSignUpConfirmation = result.showSignUpConfirmation;
    }
  }

  onCycleSignUpConfirm(): void {
    this.showSignUpConfirmation = false;
    this.cdr.markForCheck();
  }
}
